import { ActionsColumn, Typography } from '@otp/ui';
import { Star } from 'src/components/common/icons';
import {
  Actions,
  AppArea,
  DeleteMostFavouriteDocument,
  MostFavouritesDocument,
  Subjects,
} from 'src/graphql/generated/graphql';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { DeleteConfirmation } from 'src/components/common/form/delete-confirmation';
import { useMutation } from '@apollo/client';
import { PartialMostFavouriteListType } from 'src/components/most/list/favourite/types';
import { Play, Schedule, Configure } from '@otp/icons';
import { ScheduledJobForm } from '../../scheduled-jobs/form/scheduled-job';
import { RunJobForm } from '../../scheduled-jobs/form/run-job';
import { AddToFavorites } from 'src/components/most/common/add-to-favorites';
import { Can } from 'src/utils/can';

type ActionsFieldProps = {
  favourite: PartialMostFavouriteListType;
};

const QUERIES_TO_REFETCH = [MostFavouritesDocument];

export const ActionsField = ({ favourite }: ActionsFieldProps) => {
  const [deleteMutation] = useMutation(DeleteMostFavouriteDocument, {
    refetchQueries: QUERIES_TO_REFETCH,
  });

  const { t } = useTranslation(undefined, {
    keyPrefix: 'most.list.favourite',
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const onDelete = () => {
    deleteMutation({
      variables: {
        id: favourite.id,
      },
    });
    setIsDeleting(false);
  };

  const jobConfig = favourite.jobConfig
    ? {
        jobConfig: {
          command: {
            commandId: favourite.jobConfig.commandId,
            name: favourite.jobConfig.name,
            type: favourite.jobConfig.type,
          },
          devices: favourite.jobConfig.devices,
          storeIds: favourite.jobConfig.stores?.map((store) => store.id) || [],
        },
      }
    : undefined;

  return (
    <Can I={Actions.Write} a={Subjects.Favourites} appArea={AppArea.Most}>
      <ActionsColumn
        icons={[
          {
            icon: <Star isActive={true} />,
            onClick: () => setIsDeleting(true),
          },
          {
            icon: (
              <AddToFavorites
                id={favourite.id}
                defaultValue={jobConfig}
                refetchQueries={[MostFavouritesDocument]}
              >
                <Configure />
              </AddToFavorites>
            ),
          },
          {
            icon: <Schedule />,
            onClick: () => setIsScheduling(true),
          },
          {
            icon: <Play />,
            onClick: () => setIsRunning(true),
          },
        ]}
      />
      <DeleteConfirmation
        objectTitle={favourite.jobConfig?.name || ''}
        objectType={t('favourites')}
        open={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={onDelete}
      />
      {isScheduling && favourite.jobConfig && (
        <ScheduledJobForm
          title={
            (
              <div className="flex">
                <Schedule className="mr-2" />
                <Typography variant="subtitle1">{t('schedule-run')}</Typography>
              </div>
            ) as any
          }
          defaultValues={jobConfig}
          onFinish={() => setIsScheduling(false)}
          onClose={() => setIsScheduling(false)}
        />
      )}
      {isRunning && (
        <RunJobForm
          title={
            (
              <div className="flex">
                <Schedule className="mr-2" />
                <Typography variant="subtitle1">{t('execute-run')}</Typography>
              </div>
            ) as any
          }
          defaultValues={jobConfig}
          onFinish={() => setIsRunning(false)}
          onClose={() => setIsRunning(false)}
        />
      )}
    </Can>
  );
};
